import { deriveValueBands } from "./sizeFamily";
import {
  Orientation,
  PATH_SEPARATOR,
  SpineLevel,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  isPlaceholderValue,
  levelsForScope,
} from "./studioTypes";

export interface AttributeCandidate {
  attr: string;
  label: string;
}

export interface InferenceOptions {
  preferredOrder?: readonly string[];

  tieMargin?: number;

  verticalBlockShare?: number;

  fragmentJoinCm?: number;

  firstPreferredValueMatch?: string | false;
  lastPreferredValueMatch?: string | false;
  firstPreferredValues?: Readonly<Record<string, readonly string[]>>;
  lastPreferredValues?: Readonly<Record<string, readonly string[]>>;

  trace?: (levelIndex: number, scored: readonly { attr: string; score: number }[]) => void;

  retainBands?: boolean;

  forcedOrder?: readonly SpineLevel[];

  rootAttr?: string;

  /** The quantity attribute (raw size). It SEQUENCES; it never takes a blocking rung while a
   *  candidate the preferred order ranks above it is still unplaced. Left in the contest, a
   *  many-valued size outscores a real block level (sub-brand) on fragments per value pair and
   *  buries it. A size TIER (Small / Regular / Family/Club) is a separate attribute and
   *  competes like any other: the tier blocks, the number orders within it. */
  quantityAttribute?: string;

  scoreBands?: Readonly<Record<string, Readonly<Record<string, string>>>>;

  quantize?: boolean;
}

export const VERTICAL_BLOCK_SHARE = 0.55;

export const measureTopToBottomShare = (
  groups: readonly (readonly StudioItem[])[],
  shelvesPerRun: ReadonlyMap<number, number>,
): number => {
  let coveredWidth = 0;
  let totalWidth = 0;
  for (const group of groups) {
    const shelvesByRun = new Map<number, Set<number>>();
    let width = 0;
    for (const item of group) {
      for (const placement of item.placements) {
        const set = shelvesByRun.get(placement.run) ?? new Set<number>();
        set.add(placement.shelf);
        shelvesByRun.set(placement.run, set);
        width += placement.widthCm;
      }
    }
    let coverage = 0;
    let runsUsed = 0;
    shelvesByRun.forEach((set, run) => {
      coverage += set.size / Math.max(shelvesPerRun.get(run) ?? 1, 1);
      runsUsed += 1;
    });
    const fullHeight = runsUsed ? coverage / runsUsed : 0;
    totalWidth += width;
    coveredWidth += width * Math.min(fullHeight, 1);
  }
  return totalWidth > 0 ? coveredWidth / totalWidth : 0;
};

const DEFAULT_TIE_MARGIN = 0.12;

export const runOffsetsOf = (runs: readonly StudioRunGeometry[]): number[] => {
  const offsets: number[] = [];
  let acc = 0;
  for (const run of [...runs].sort((a, b) => a.run - b.run)) {
    offsets[run.run] = acc;
    acc += run.widthCm;
  }
  return offsets;
};

const laneSequences = (items: readonly StudioItem[]) => {
  const lanes = new Map<string, { xCm: number; item: StudioItem }[]>();
  for (const item of items) {
    for (const placement of item.placements) {
      const key = `${placement.run}|${placement.shelf}`;
      const lane = lanes.get(key);
      const entry = { xCm: placement.xCm, item };
      if (lane) lane.push(entry);
      else lanes.set(key, [entry]);
    }
  }
  for (const lane of lanes.values()) lane.sort((a, b) => a.xCm - b.xCm);
  return lanes;
};

interface Cleanliness {
  returns: number;

  inversions: number;
}

export const measureCleanliness = (items: readonly StudioItem[], attr: string): Cleanliness => {
  let returns = 0;
  let inversions = 0;
  const laneOrders: string[][] = [];
  for (const lane of laneSequences(items).values()) {
    const seen = new Set<string>();
    let previous: string | null = null;
    const firstSeen: string[] = [];
    for (const { item } of lane) {
      const value = item.attrs[attr] ?? "";
      if (value !== previous) {
        if (seen.has(value)) returns += 1;
        else {
          seen.add(value);
          firstSeen.push(value);
        }
        previous = value;
      }
    }
    laneOrders.push(firstSeen);
  }

  for (let a = 0; a < laneOrders.length; a++) {
    for (let b = a + 1; b < laneOrders.length; b++) {
      const rankB = new Map(laneOrders[b].map((value, index) => [value, index]));
      const shared = laneOrders[a].filter((value) => rankB.has(value));
      for (let i = 0; i < shared.length; i++) {
        for (let j = i + 1; j < shared.length; j++) {
          if ((rankB.get(shared[i]) ?? 0) > (rankB.get(shared[j]) ?? 0)) inversions += 1;
        }
      }
    }
  }
  return { returns, inversions };
};

const DEFAULT_FRAGMENT_JOIN_CM = 20;

export const measureExtraFragments = (
  items: readonly StudioItem[],
  attr: string,

  bandOrFragmentJoinCm?: Readonly<Record<string, string>> | number,
  configuredFragmentJoinCm: number = DEFAULT_FRAGMENT_JOIN_CM,
): number => {
  const bandOf = typeof bandOrFragmentJoinCm === "number" ? undefined : bandOrFragmentJoinCm;
  const fragmentJoinCm =
    typeof bandOrFragmentJoinCm === "number" ? bandOrFragmentJoinCm : configuredFragmentJoinCm;
  interface Box {
    run: number;
    shelf: number;
    x0: number;
    x1: number;
  }
  const byValue = new Map<string, Box[]>();
  for (const item of items) {
    const raw = item.attrs[attr] ?? "";
    const value = bandOf?.[raw] ?? raw;
    const boxes = byValue.get(value) ?? [];
    for (const placement of item.placements) {
      boxes.push({
        run: placement.run,
        shelf: placement.shelf,
        x0: placement.xCm,
        x1: placement.xCm + placement.widthCm,
      });
    }
    byValue.set(value, boxes);
  }
  let extra = 0;
  byValue.forEach((boxes) => {
    const parent = boxes.map((_, index) => index);
    const find = (index: number): number =>
      parent[index] === index ? index : (parent[index] = find(parent[index]));
    for (let i = 0; i < boxes.length; i++) {
      for (let j = i + 1; j < boxes.length; j++) {
        const a = boxes[i];
        const b = boxes[j];
        if (a.run !== b.run || Math.abs(a.shelf - b.shelf) > 1) continue;
        if (a.x0 <= b.x1 + fragmentJoinCm && b.x0 <= a.x1 + fragmentJoinCm) {
          parent[find(i)] = find(j);
        }
      }
    }
    const roots = new Set(boxes.map((_, index) => find(index)));
    extra += Math.max(roots.size - 1, 0);
  });
  return extra;
};

export const measureBlockShare = (
  items: readonly StudioItem[],
  attr: string,
  fragmentJoinCm: number = DEFAULT_FRAGMENT_JOIN_CM,
): number => {
  interface Box {
    run: number;
    shelf: number;
    x0: number;
    x1: number;
    width: number;
  }
  const byValue = new Map<string, Box[]>();
  for (const item of items) {
    const value = item.attrs[attr];
    if (value === null || value === undefined) continue;
    const boxes = byValue.get(value) ?? [];
    for (const placement of item.placements) {
      boxes.push({
        run: placement.run,
        shelf: placement.shelf,
        x0: placement.xCm,
        x1: placement.xCm + placement.widthCm,
        width: placement.widthCm,
      });
    }
    byValue.set(value, boxes);
  }
  let weighted = 0;
  let totalWidth = 0;
  byValue.forEach((boxes) => {
    const parent = boxes.map((_, index) => index);
    const find = (index: number): number =>
      parent[index] === index ? index : (parent[index] = find(parent[index]));
    for (let i = 0; i < boxes.length; i++) {
      for (let j = i + 1; j < boxes.length; j++) {
        const a = boxes[i];
        const b = boxes[j];
        if (a.run !== b.run || Math.abs(a.shelf - b.shelf) > 1) continue;
        if (a.x0 <= b.x1 + fragmentJoinCm && b.x0 <= a.x1 + fragmentJoinCm) {
          parent[find(i)] = find(j);
        }
      }
    }
    const widthByRegion = new Map<number, number>();
    let valueWidth = 0;
    boxes.forEach((box, index) => {
      const root = find(index);
      widthByRegion.set(root, (widthByRegion.get(root) ?? 0) + box.width);
      valueWidth += box.width;
    });
    if (valueWidth <= 0) return;
    const largest = Math.max(...widthByRegion.values());
    weighted += largest;
    totalWidth += valueWidth;
  });
  return totalWidth > 0 ? weighted / totalWidth : 0;
};

export const inferValueOrder = (
  items: readonly StudioItem[],
  attr: string,
  runOffsets: readonly number[],
  options: Pick<
    InferenceOptions,
    | "fragmentJoinCm"
    | "firstPreferredValueMatch"
    | "lastPreferredValueMatch"
    | "firstPreferredValues"
    | "lastPreferredValues"
  > = {},
): string[] => {
  const fragmentJoinCm = options.fragmentJoinCm ?? DEFAULT_FRAGMENT_JOIN_CM;
  const stretches = new Map<string, { start: number; end: number; width: number }[]>();
  for (const item of items) {
    const value = item.attrs[attr];
    if (value === null || value === undefined) continue;
    const list = stretches.get(value) ?? [];
    for (const placement of item.placements) {
      const start = runOffsets[placement.run] + placement.xCm;
      list.push({ start, end: start + placement.widthCm, width: placement.widthCm });
    }
    stretches.set(value, list);
  }
  const anchors = new Map<string, number | null>();
  for (const [value, list] of stretches) {
    list.sort((a, b) => a.start - b.start);

    let best: { width: number; weighted: number } | null = null;
    let current = { end: -Infinity, width: 0, weighted: 0 };
    const flush = () => {
      if (current.width > 0 && (best === null || current.width > best.width)) {
        best = { width: current.width, weighted: current.weighted };
      }
    };
    for (const stretch of list) {
      if (stretch.start - current.end > fragmentJoinCm) {
        flush();
        current = { end: stretch.end, width: stretch.width, weighted: 0 };
        current.weighted = ((stretch.start + stretch.end) / 2) * stretch.width;
      } else {
        current.end = Math.max(current.end, stretch.end);
        current.width += stretch.width;
        current.weighted += ((stretch.start + stretch.end) / 2) * stretch.width;
      }
    }
    flush();
    const winner = best as { width: number; weighted: number } | null;

    // Null anchor trails placed values; a mean would let overlays drag the order.
    anchors.set(value, winner ? winner.weighted / winner.width : null);
  }
  const measured = [...anchors.entries()]
    .sort((a, b) => (a[1] ?? Infinity) - (b[1] ?? Infinity))
    .map(([value, anchor]) => ({ value, anchor }));
  return applyPreferredValueRules(measured, attr, options);
};

const preferredValuePattern = (configured: string | false | undefined) =>
  typeof configured === "string" && configured ? new RegExp(configured, "i") : null;

/** FIRST is a tie-break only; LAST keeps override strength. */
export const houseRuleFirst = (
  values: readonly string[],
  options: Pick<InferenceOptions, "firstPreferredValueMatch" | "lastPreferredValueMatch"> = {},
): string[] => {
  const first = preferredValuePattern(options.firstPreferredValueMatch);
  const last = preferredValuePattern(options.lastPreferredValueMatch);
  const isFirst = (value: string) => first?.test(value.trim()) ?? false;
  const isLast = (value: string) => last?.test(value.trim()) ?? false;
  return [
    ...values.filter(isFirst),
    ...values.filter((value) => !isFirst(value) && !isLast(value)),
    ...values.filter((value) => !isFirst(value) && isLast(value)),
  ];
};

const valuesMatchingInRuleOrder = (
  values: readonly string[],
  rules: readonly string[],
): string[] => {
  const remaining = new Set(values);
  const ordered: string[] = [];
  for (const rule of rules) {
    const needle = rule.trim().toLowerCase();
    if (!needle) continue;
    for (const value of values) {
      if (remaining.has(value) && value.toLowerCase().includes(needle)) {
        ordered.push(value);
        remaining.delete(value);
      }
    }
  }
  return ordered;
};

interface MeasuredValue {
  value: string;
  anchor: number | null;
}

const ANCHOR_TIE_CM = 1e-6;

const applyPreferredValueRules = (
  measured: readonly MeasuredValue[],
  attr: string,
  options: Pick<
    InferenceOptions,
    | "firstPreferredValueMatch"
    | "lastPreferredValueMatch"
    | "firstPreferredValues"
    | "lastPreferredValues"
  >,
): string[] => {
  // FIRST only within tied anchors; LAST still overrides.
  const tieBroken: string[] = [];
  let group: MeasuredValue[] = [];
  const flushGroup = () => {
    if (group.length === 0) return;
    tieBroken.push(
      ...houseRuleFirst(
        group.map((entry) => entry.value),
        options,
      ),
    );
    group = [];
  };
  for (const entry of measured) {
    const previous = group[group.length - 1];
    const tied =
      previous !== undefined &&
      (previous.anchor === null
        ? entry.anchor === null
        : entry.anchor !== null && Math.abs(entry.anchor - previous.anchor) <= ANCHOR_TIE_CM);
    if (previous !== undefined && !tied) flushGroup();
    group.push(entry);
  }
  flushGroup();

  const last = preferredValuePattern(options.lastPreferredValueMatch);
  const isLast = (value: string) => last?.test(value.trim()) ?? false;
  const regexOrdered = [
    ...tieBroken.filter((value) => !isLast(value)),
    ...tieBroken.filter(isLast),
  ];

  const first = valuesMatchingInRuleOrder(regexOrdered, options.firstPreferredValues?.[attr] ?? []);
  const firstSet = new Set(first);
  const afterFirst = regexOrdered.filter((value) => !firstSet.has(value));
  const lastListed = valuesMatchingInRuleOrder(
    afterFirst,
    options.lastPreferredValues?.[attr] ?? [],
  );
  const lastSet = new Set(lastListed);
  return [...first, ...afterFirst.filter((value) => !lastSet.has(value)), ...lastListed];
};

export const inferReadingOrder = (items: readonly StudioItem[], attr: string): string[] => {
  const entries: { run: number; shelf: number; xCm: number; value: string }[] = [];
  for (const item of items) {
    const value = item.attrs[attr];
    if (value === null || value === undefined) continue;
    for (const placement of item.placements) {
      entries.push({ run: placement.run, shelf: placement.shelf, xCm: placement.xCm, value });
    }
  }
  entries.sort((a, b) => a.run - b.run || a.shelf - b.shelf || a.xCm - b.xCm);
  const seen = new Set<string>();
  const order: string[] = [];
  for (const entry of entries) {
    if (!seen.has(entry.value)) {
      seen.add(entry.value);
      order.push(entry.value);
    }
  }
  return order;
};

export const inferVerticalValueOrder = (
  items: readonly StudioItem[],
  attr: string,
  runOffsets: readonly number[],
): string[] => {
  const totals = new Map<string, { shelfWeighted: number; xWeighted: number; width: number }>();
  for (const item of items) {
    const value = item.attrs[attr];
    if (value === null || value === undefined) continue;
    for (const placement of item.placements) {
      const weight = placement.widthCm;
      const flowX = runOffsets[placement.run] + placement.xCm + placement.widthCm / 2;
      const total = totals.get(value);
      if (total) {
        total.shelfWeighted += placement.shelf * weight;
        total.xWeighted += flowX * weight;
        total.width += weight;
      } else {
        totals.set(value, {
          shelfWeighted: placement.shelf * weight,
          xWeighted: flowX * weight,
          width: weight,
        });
      }
    }
  }

  return [...totals.entries()]
    .sort((a, b) => {
      const shelfDelta = a[1].shelfWeighted / a[1].width - b[1].shelfWeighted / b[1].width;
      if (Math.abs(shelfDelta) > 0.5) return shelfDelta;
      return a[1].xWeighted / a[1].width - b[1].xWeighted / b[1].width;
    })
    .map(([value]) => value);
};

interface OrientationMeasure {
  orientation: Orientation;

  confidence: number;

  xSeparation: number;

  shelfSeparation: number;
}

export const measureOrientation = (
  groups: readonly (readonly StudioItem[])[],
  runOffsets: readonly number[],
): OrientationMeasure => {
  const extents = groups.map((group) => {
    let min = Infinity;
    let max = -Infinity;
    const shelves = new Set<string>();
    for (const item of group) {
      for (const placement of item.placements) {
        const start = runOffsets[placement.run] + placement.xCm;
        min = Math.min(min, start);
        max = Math.max(max, start + placement.widthCm);
        shelves.add(`${placement.run}|${placement.shelf}`);
      }
    }
    return { min, max, shelves };
  });
  let xOverlap = 0;
  let xSpan = 0;
  let shelfShared = 0;
  let shelfPairs = 0;
  for (let i = 0; i < extents.length; i++) {
    for (let j = i + 1; j < extents.length; j++) {
      const a = extents[i];
      const b = extents[j];
      const overlap = Math.max(0, Math.min(a.max, b.max) - Math.max(a.min, b.min));
      xOverlap += overlap;
      xSpan += Math.min(a.max - a.min, b.max - b.min);
      const shared = [...a.shelves].filter((shelf) => b.shelves.has(shelf)).length;
      shelfShared += shared;
      shelfPairs += Math.min(a.shelves.size, b.shelves.size);
    }
  }
  const xSeparation = xSpan > 0 ? 1 - xOverlap / xSpan : 1;
  const shelfSeparation = shelfPairs > 0 ? 1 - shelfShared / shelfPairs : 0;
  if (shelfSeparation > xSeparation) {
    return {
      orientation: "vertical",
      confidence: shelfSeparation - xSeparation,
      xSeparation,
      shelfSeparation,
    };
  }
  return {
    orientation: "horizontal",
    confidence: xSeparation - shelfSeparation,
    xSeparation,
    shelfSeparation,
  };
};

interface ScopedGroup {
  path: readonly string[];
  items: readonly StudioItem[];
}

const splitScope = (scope: ScopedGroup, attr: string, order: readonly string[]): ScopedGroup[] => {
  const groups = new Map<string, StudioItem[]>();
  for (const item of scope.items) {
    const value = item.attrs[attr] ?? "";
    const group = groups.get(value);
    if (group) group.push(item);
    else groups.set(value, [item]);
  }
  const rank = new Map(order.map((value, index) => [value, index]));
  return [...groups.entries()]
    .sort((a, b) => (rank.get(a[0]) ?? order.length) - (rank.get(b[0]) ?? order.length))
    .map(([value, items]) => ({ path: [...scope.path, value], items }));
};

const NEAR_CONSTANT_SHARE = 0.8;

const BRANCH_BLOCK_MARGIN = 0.1;

const preferredRankOf = (preferred: readonly string[]) => (attr: string) => {
  const index = preferred.indexOf(attr);
  return index < 0 ? preferred.length : index;
};

/** A candidate whose values fragment more often than they split is not a block level: it
 *  cannot hold the quantity attribute back. Fragments per value pair, so -1 is "one stray
 *  stretch per value" — Detergent's sub-brand (55 strays over 31 values) fails this, Fabric
 *  care's (2 strays over 19 values) passes. Judged at SECTION level, not at the depth where
 *  size is contested: deep inside brand › form › pack type the scopes are tiny and almost
 *  anything looks contiguous there. */
export const BLOCK_WORTHY_SCORE = -1;

/** Fragments per value pair for one attribute over one group; null when it does not split. */
const fragmentScoreOf = (
  group: readonly StudioItem[],
  attr: string,
  bandOf: Readonly<Record<string, string>> | undefined,
  fragmentJoinCm: number,
): number | null => {
  // Items that carry no value for this attribute are not a block of their own: a filler
  // ("Original" as no sub-brand, once blanked) scattered along the shelf must not count as
  // fragments against the attribute that the real values partition cleanly.
  const values = new Set<string>();
  const carrying: StudioItem[] = [];
  for (const item of group) {
    const raw = item.attrs[attr];
    if (raw === null || raw === undefined || isPlaceholderValue(raw)) continue;
    carrying.push(item);
    values.add(bandOf?.[raw] ?? raw);
  }
  if (values.size < 2) return null;
  return -measureExtraFragments(carrying, attr, bandOf, fragmentJoinCm) / (values.size - 1);
};

/** Block-worthiness across the root sections, weighted by items: an attribute that is a clean
 *  block in the sections holding most of the planogram may hold size back; one that is a
 *  jumble there may not, however tidy it looks deep in the tree. */
const sectionBlockWorthy = (
  items: readonly StudioItem[],
  rootAttr: string,
  attr: string,
  bandOf: Readonly<Record<string, string>> | undefined,
  fragmentJoinCm: number,
): boolean => {
  const sections = new Map<string, StudioItem[]>();
  for (const item of items) {
    const key = String(item.attrs[rootAttr] ?? "");
    const list = sections.get(key);
    if (list) list.push(item);
    else sections.set(key, [item]);
  }
  let weighted = 0;
  let weight = 0;
  for (const group of sections.values()) {
    const score = fragmentScoreOf(group, attr, bandOf, fragmentJoinCm);
    if (score === null) continue;
    weighted += score * group.length;
    weight += group.length;
  }
  return weight > 0 && weighted / weight > BLOCK_WORTHY_SCORE;
};

/** Candidates allowed to win the next rung. The quantity attribute is held back while any
 *  other remaining BLOCK-WORTHY candidate sits above it in the preferred order; attributes
 *  the order does not name rank last and never hold it back. */
export const rungContenders = <T extends { attr: string }>(
  remaining: readonly T[],
  _preferred: readonly string[],
  quantity: string | undefined,
  blockWorthy: (attr: string) => boolean = () => true,
): T[] => {
  if (quantity === undefined || !remaining.some((candidate) => candidate.attr === quantity)) {
    return [...remaining];
  }
  // Raw size is never a tier. It waits until no block-worthy level is left, wherever the
  // preferred order lists it: a size TIER, flavour or sub-brand that genuinely blocks the
  // shelf all come first; a jumbled attribute does not hold it back.
  const heldBack = remaining.some(
    (candidate) => candidate.attr !== quantity && blockWorthy(candidate.attr),
  );
  return heldBack ? remaining.filter((candidate) => candidate.attr !== quantity) : [...remaining];
};

/** Re-seats the quantity attribute in an order a cost search may have lifted it in: directly
 *  after the last block-worthy level. */
export const pinQuantity = (
  order: readonly string[],
  quantity: string | undefined,
  blockWorthy: (attr: string) => boolean = () => true,
): string[] => {
  if (quantity === undefined || !order.includes(quantity)) return [...order];
  const rest = order.filter((attr) => attr !== quantity);
  let at = -1;
  rest.forEach((attr, index) => {
    if (blockWorthy(attr)) at = index;
  });
  return [...rest.slice(0, at + 1), quantity, ...rest.slice(at + 1)];
};

const rankBranch = (
  branchItems: readonly StudioItem[],
  pool: readonly AttributeCandidate[],
  preferred: readonly string[],
  fragmentJoinCm: number,
  quantity?: string,
): { attrs: string[]; decisive: boolean[] } | null => {
  if (branchItems.length === 0) return null;
  const preferredIndex = preferredRankOf(preferred);
  let scopes: readonly StudioItem[][] = [[...branchItems]];
  const remaining = [...pool];
  const attrs: string[] = [];
  const decisive: boolean[] = [];
  while (remaining.length > 0) {
    // Scored on the spine's own measure — extra shelf fragments per value pair — not on
    // block share. Block share saturates on a real aisle (every candidate 0.88–0.99), the
    // margin swallows the differences and the preferred order decides every rung, so a
    // section never gets an order of its own. Fragments per pair separates a level that
    // partitions the section (Fabric care sub-brand, 2 strays over 19 values) from one that
    // does not. The quantity attribute is kept out of the contest (see `rungContenders`).
    const scored = remaining.map((candidate) => {
      let share = 0;
      let weight = 0;
      let extra = 0;
      let valuePairs = 0;
      let usable = false;
      for (const scope of scopes) {
        if (scope.length <= 1) continue;

        weight += scope.length;
        const counts = new Map<string, number>();
        for (const item of scope) {
          const value = item.attrs[candidate.attr];
          if (value === null || value === undefined) continue;
          counts.set(value, (counts.get(value) ?? 0) + 1);
        }
        const carried = [...counts.values()].reduce((a, b) => a + b, 0);
        if (counts.size < 2 || carried === 0) continue;
        const biggest = Math.max(...counts.values());
        if (biggest / carried >= NEAR_CONSTANT_SHARE) continue;

        if (carried >= MIN_ITEMS_TO_JUDGE_GENERICITY && counts.size / carried > NEAR_UNIQUE_RATIO) {
          continue;
        }
        usable = true;
        if (quantity === undefined) {
          share += measureBlockShare(scope, candidate.attr, fragmentJoinCm) * scope.length;
        } else {
          extra += measureExtraFragments(scope, candidate.attr, fragmentJoinCm);
          valuePairs += counts.size - 1;
        }
      }
      // Without a pinned quantity attribute the fragment measure would let a many-valued size
      // take a rung (Detergent: brand > form > size > pack type), so callers that configure no
      // quantity keep the block-share ranking they were built against.
      return {
        candidate,
        // Unusable candidates sit below every usable one; ties among them fall to preference.
        score:
          quantity === undefined
            ? usable && weight > 0
              ? share / weight
              : -1
            : usable
              ? -extra / Math.max(valuePairs, 1)
              : Number.NEGATIVE_INFINITY,
        usable,
      };
    });
    const eligible = new Set(
      rungContenders(
        remaining,
        preferred,
        quantity,
        // Any level that still splits this branch comes before raw size.
        (attr) => scored.find((entry) => entry.candidate.attr === attr)?.usable ?? false,
      ).map((candidate) => candidate.attr),
    );
    const inContest = scored.filter((entry) => eligible.has(entry.candidate.attr));
    const best = inContest.reduce((a, b) => (b.score > a.score ? b : a));
    // With a pinned quantity the merchant's declared order decides the nesting among the
    // levels that are block-worthy here (fewer strays than value pairs); the measure only
    // demotes a jumble. Contiguity cannot tell "sub-brand blocks holding scents" from "scent
    // blocks holding sub-brands" — both read clean — so a flavour with many values must not
    // outrank the sub-brand the profile puts above it on fragments-per-pair alone.
    const worthy =
      quantity === undefined
        ? []
        : inContest.filter((entry) => entry.usable && entry.score > BLOCK_WORTHY_SCORE);
    const contenders =
      worthy.length > 0
        ? worthy
        : inContest.filter((entry) => entry.score >= best.score - BRANCH_BLOCK_MARGIN);
    const winner = contenders.reduce((a, b) =>
      preferredIndex(a.candidate.attr) <= preferredIndex(b.candidate.attr) ? a : b,
    );
    attrs.push(winner.candidate.attr);
    decisive.push(winner.usable && contenders.length === 1);
    remaining.splice(
      remaining.findIndex((candidate) => candidate.attr === winner.candidate.attr),
      1,
    );
    scopes = scopes.flatMap((scope) => {
      const groups = new Map<string, StudioItem[]>();
      for (const item of scope) {
        const value = item.attrs[winner.candidate.attr] ?? "";
        const group = groups.get(value);
        if (group) group.push(item);
        else groups.set(value, [item]);
      }
      return groups.size < 2 ? [scope] : [...groups.values()];
    });
  }
  return { attrs, decisive };
};

const treeCost = (
  branchItems: readonly StudioItem[],
  order: readonly string[],
  fragmentJoinCm: number,
): number => {
  let cost = 0;
  let scopes: readonly StudioItem[][] = [[...branchItems]];
  for (const attr of order) {
    for (const scope of scopes) {
      if (scope.length <= 1) continue;
      if (new Set(scope.map((item) => item.attrs[attr] ?? "")).size < 2) continue;
      cost += measureExtraFragments(scope, attr, undefined, fragmentJoinCm);
    }
    scopes = scopes.flatMap((scope) => {
      const groups = new Map<string, StudioItem[]>();
      for (const item of scope) {
        const value = item.attrs[attr] ?? "";
        const group = groups.get(value);
        if (group) group.push(item);
        else groups.set(value, [item]);
      }
      return groups.size < 2 ? [scope] : [...groups.values()];
    });
  }
  return cost;
};

const refineBranchOrder = (
  branchItems: readonly StudioItem[],
  attrs: readonly string[],
  fragmentJoinCm: number,
): { attrs: string[]; cost: number } => {
  let best = [...attrs];
  let bestCost = treeCost(branchItems, best, fragmentJoinCm);
  let improved = true;
  while (improved && bestCost > 0) {
    improved = false;
    for (let index = 0; index + 1 < best.length; index += 1) {
      const swapped = [...best];
      [swapped[index], swapped[index + 1]] = [swapped[index + 1], swapped[index]];
      const cost = treeCost(branchItems, swapped, fragmentJoinCm);
      if (cost < bestCost) {
        best = swapped;
        bestCost = cost;
        improved = true;
      }
    }
  }
  return { attrs: best, cost: bestCost };
};

export interface InferredSpine {
  spine: SpineLevel[];

  scopedLevelOrder: Record<string, string[]>;
  valueOrder: Record<string, string[]>;
  scopedValueOrder: Record<string, string[]>;
  bandOverrides: Record<string, number[]>;
  orientOverrides: Record<string, Orientation>;
  shelfHints: Record<string, { shelf: number; hFacings: number }[]>;

  levelBands: Record<string, Record<string, string>>;
}

export const NEAR_UNIQUE_RATIO = 0.25;

export const MIN_ITEMS_TO_JUDGE_GENERICITY = 32;

const distinctValueRatio = (
  items: readonly StudioItem[],
  attr: string,
  bandOf?: Readonly<Record<string, string>>,
): number => {
  const values = new Set<string>();
  let carried = 0;
  for (const item of items) {
    const raw = item.attrs[attr];
    if (raw == null) continue;
    carried += 1;
    values.add(bandOf?.[raw] ?? raw);
  }
  return carried > 0 ? values.size / carried : 0;
};

export interface LevelQuantization {
  levelBands: Record<string, Record<string, string>>;

  excluded: readonly string[];
}

/** Items a value needs before it can be a block of its own. */
export const MIN_BLOCKABLE_ITEMS = 2;

/** Where the one-offs pool; the tree draws this node. */
export const RARITY_POOL_LABEL = "Other";

/** Banding for attributes with no magnitude: values repeated often enough to hold a
 *  block keep themselves, the one-offs pool. Only pooled values are mapped, since
 *  `distinctValueRatio` leaves anything unmapped as itself. */
export const deriveRarityBands = (
  values: readonly (string | null | undefined)[],
): Record<string, string> => {
  const counts = new Map<string, number>();
  for (const raw of values) {
    if (raw == null) continue;
    counts.set(raw, (counts.get(raw) ?? 0) + 1);
  }
  const bands: Record<string, string> = {};
  for (const [value, count] of counts) {
    if (count < MIN_BLOCKABLE_ITEMS) bands[value] = RARITY_POOL_LABEL;
  }
  // Everything pooled means the attribute is unique per item, not bandable.
  return Object.keys(bands).length === counts.size ? {} : bands;
};

/** A near-unique attribute is excluded only if still near-unique after banding. */
export const quantizeCandidates = (
  items: readonly StudioItem[],
  candidates: readonly AttributeCandidate[],
): LevelQuantization => {
  const levelBands: Record<string, Record<string, string>> = {};
  const excluded: string[] = [];
  if (items.length < MIN_ITEMS_TO_JUDGE_GENERICITY) return { levelBands, excluded };
  for (const candidate of candidates) {
    const carrying = items.filter((item) => item.attrs[candidate.attr] != null).length;
    if (carrying < MIN_ITEMS_TO_JUDGE_GENERICITY) continue;
    if (distinctValueRatio(items, candidate.attr) <= NEAR_UNIQUE_RATIO) continue;
    const { bandOf } = deriveValueBands(items.map((item) => item.attrs[candidate.attr]));
    const banded = Object.fromEntries(bandOf);
    if (bandOf.size > 0 && distinctValueRatio(items, candidate.attr, banded) <= NEAR_UNIQUE_RATIO) {
      levelBands[candidate.attr] = banded;
      continue;
    }
    const rarity = deriveRarityBands(items.map((item) => item.attrs[candidate.attr]));
    if (
      Object.keys(rarity).length > 0 &&
      distinctValueRatio(items, candidate.attr, rarity) <= NEAR_UNIQUE_RATIO
    ) {
      levelBands[candidate.attr] = rarity;
      continue;
    }
    excluded.push(candidate.attr);
  }
  return { levelBands, excluded };
};

const bandedView = (
  items: readonly StudioItem[],
  levelBands: Record<string, Record<string, string>>,
): readonly StudioItem[] => {
  const attrs = Object.keys(levelBands);
  if (attrs.length === 0) return items;
  return items.map((item) => {
    const next = { ...item.attrs };
    let touched = false;
    for (const attr of attrs) {
      const raw = next[attr];
      if (raw == null) continue;
      const band = levelBands[attr][raw];
      if (band !== undefined && band !== raw) {
        next[attr] = band;
        touched = true;
      }
    }
    return touched ? { ...item, attrs: next } : item;
  });
};

const BAND_SHARE_THRESHOLD = 0.1;

export const annotateContract = (
  items: readonly StudioItem[],
  spine: readonly SpineLevel[],
  runOffsets: readonly number[],
  out: Pick<InferredSpine, "scopedValueOrder" | "bandOverrides" | "orientOverrides">,
  levelIndex = 0,
  path: readonly string[] = [],

  bandShareThreshold: number = BAND_SHARE_THRESHOLD,
  inferenceOptions: Pick<
    InferenceOptions,
    | "fragmentJoinCm"
    | "firstPreferredValueMatch"
    | "lastPreferredValueMatch"
    | "firstPreferredValues"
    | "lastPreferredValues"
  > = {},

  scopedLevelOrder?: Readonly<Record<string, readonly string[]>>,
) => {
  const levels = levelsForScope(spine, scopedLevelOrder, path);
  if (levelIndex >= levels.length || items.length <= 1) return;
  const level = levels[levelIndex];
  const groups = new Map<string, StudioItem[]>();
  for (const item of items) {
    const value = item.attrs[level.attr] ?? "";
    const group = groups.get(value);
    if (group) group.push(item);
    else groups.set(value, [item]);
  }
  if (groups.size < 2) {
    annotateContract(
      items,
      spine,
      runOffsets,
      out,
      levelIndex + 1,
      path,
      bandShareThreshold,
      inferenceOptions,
      scopedLevelOrder,
    );
    return;
  }
  const pathKey = path.join(PATH_SEPARATOR);
  const measured = measureOrientation([...groups.values()], runOffsets);
  if (measured.orientation !== level.orient) {
    out.orientOverrides[pathKey] = measured.orientation;
  }
  const order =
    measured.orientation === "vertical"
      ? inferVerticalValueOrder(items, level.attr, runOffsets)
      : measured.xSeparation > 0.5
        ? inferValueOrder(items, level.attr, runOffsets, inferenceOptions)
        : inferReadingOrder(items, level.attr);
  out.scopedValueOrder[pathKey] = order;
  if (measured.orientation === "vertical") {
    for (const [value, group] of groups) {
      const widths = new Map<number, number>();
      let total = 0;
      for (const item of group) {
        for (const placement of item.placements) {
          widths.set(placement.shelf, (widths.get(placement.shelf) ?? 0) + placement.widthCm);
          total += placement.widthCm;
        }
      }
      const band = [...widths.entries()]
        .filter(([, width]) => width >= total * bandShareThreshold)
        .map(([rank]) => rank)
        .sort((a, b) => a - b);
      if (band.length > 0) {
        out.bandOverrides[[...path, value].join(PATH_SEPARATOR)] = band;
      }
    }
  }
  for (const [value, group] of groups) {
    annotateContract(
      group,
      spine,
      runOffsets,
      out,
      levelIndex + 1,
      [...path, value],
      bandShareThreshold,
      inferenceOptions,
      scopedLevelOrder,
    );
  }
};

const inferOverView = (
  items: readonly StudioItem[],
  candidates: readonly AttributeCandidate[],
  runs: readonly StudioRunGeometry[],
  options: InferenceOptions = {},
): InferredSpine => {
  const tieMargin = options.tieMargin ?? DEFAULT_TIE_MARGIN;
  const verticalBlockShare = options.verticalBlockShare ?? VERTICAL_BLOCK_SHARE;
  const fragmentJoinCm = options.fragmentJoinCm ?? DEFAULT_FRAGMENT_JOIN_CM;
  const preferred = options.preferredOrder ?? candidates.map((candidate) => candidate.attr);
  const runOffsets = runOffsetsOf(runs);

  const valueOrder: Record<string, string[]> = {};
  for (const candidate of candidates) {
    valueOrder[candidate.attr] = inferValueOrder(items, candidate.attr, runOffsets, options);
  }

  const orientOverrides: Record<string, Orientation> = {};

  const rank = (
    initialScopes: readonly ScopedGroup[],
    pool: readonly AttributeCandidate[],
    depthOffset: number,
    limit?: number,
  ): { levels: SpineLevel[]; decisive: boolean[] } => {
    let scopes: ScopedGroup[] = [...initialScopes];
    const remaining = [...pool];
    const spine: SpineLevel[] = [];
    const decisive: boolean[] = [];
    while (remaining.length > 0 && (limit === undefined || spine.length < limit)) {
      const scored = remaining.map((candidate) => {
        let extra = 0;
        let valuePairs = 0;
        let differentiates = false;

        // Bands affect score only; scopes and shipped values stay raw.
        const bandOf = options.scoreBands?.[candidate.attr];
        for (const scope of scopes) {
          const values = new Set(scope.items.map((item) => item.attrs[candidate.attr] ?? ""));
          if (values.size < 2) continue;
          differentiates = true;
          extra += measureExtraFragments(scope.items, candidate.attr, bandOf, fragmentJoinCm);
          valuePairs +=
            (bandOf ? new Set([...values].map((v) => bandOf[v] ?? v)).size : values.size) - 1;
        }

        return {
          candidate,
          score: differentiates ? -extra / Math.max(valuePairs, 1) : 0,
          differentiates,
        };
      });
      options.trace?.(
        depthOffset + spine.length,
        scored.map((entry) => ({ attr: entry.candidate.attr, score: entry.score })),
      );
      const eligible = new Set(
        rungContenders(
          remaining,
          preferred,
          options.quantityAttribute,
          // Raw size is the sequencing level: every attribute that still splits a scope at
          // this depth takes its rung first. Only size TIER is a tier.
          (attr) => scored.find((entry) => entry.candidate.attr === attr)?.differentiates ?? false,
        ).map((candidate) => candidate.attr),
      );
      const inContest = scored.filter((entry) => eligible.has(entry.candidate.attr));
      let best = inContest[0];
      for (const entry of inContest) if (entry.score > best.score) best = entry;

      const slack = Math.max(0.15, Math.abs(best.score) * tieMargin);
      // With a pinned quantity, every block-worthy splitter is a contender and the declared
      // order decides among them (see rankBranch). Attributes that split nothing here score 0
      // but are not splitters, so they no longer outrank real levels.
      // Worthiness is judged at section level (as for the pin): deep in the tree the scopes
      // are tiny and a jumbled attribute looks contiguous, which would let Detergent's
      // sub-brand claim a rung above size on declaration alone.
      const rootAttr = spine[0]?.attr ?? options.rootAttr;
      const worthy =
        options.quantityAttribute === undefined || rootAttr === undefined
          ? []
          : inContest.filter(
              (entry) =>
                entry.differentiates &&
                sectionBlockWorthy(
                  items,
                  rootAttr,
                  entry.candidate.attr,
                  options.scoreBands?.[entry.candidate.attr],
                  fragmentJoinCm,
                ),
            );
      const contenders =
        worthy.length > 0 ? worthy : inContest.filter((entry) => entry.score >= best.score - slack);
      const preferredIndex = (attr: string) => {
        const index = preferred.indexOf(attr);
        return index < 0 ? preferred.length : index;
      };

      const configuredRoot = options.rootAttr;
      const assertedRoot =
        depthOffset + spine.length === 0 &&
        configuredRoot !== undefined &&
        scored.some(
          (entry) =>
            entry.candidate.attr === configuredRoot &&
            new Set(items.map((item) => item.attrs[configuredRoot] ?? "")).size > 1,
        )
          ? configuredRoot
          : undefined;
      const forcedAttr = options.forcedOrder?.[depthOffset + spine.length]?.attr ?? assertedRoot;
      const winner =
        (forcedAttr !== undefined
          ? scored.find((entry) => entry.candidate.attr === forcedAttr)
          : undefined) ??
        contenders.reduce((a, b) =>
          preferredIndex(a.candidate.attr) <= preferredIndex(b.candidate.attr) ? a : b,
        );

      const measures: { path: readonly string[]; measure: OrientationMeasure }[] = [];
      for (const scope of scopes) {
        const groups = splitScope(scope, winner.candidate.attr, valueOrder[winner.candidate.attr]);
        if (groups.length < 2) continue;
        measures.push({
          path: scope.path,
          measure: measureOrientation(
            groups.map((group) => group.items),
            runOffsets,
          ),
        });
      }
      const horizontalVotes = measures.filter((m) => m.measure.orientation === "horizontal").length;

      const valueGroups: StudioItem[][] = [];
      for (const scope of scopes) {
        const groups = splitScope(scope, winner.candidate.attr, valueOrder[winner.candidate.attr]);
        if (groups.length < 2) continue;
        for (const group of groups) valueGroups.push([...group.items]);
      }
      const shelvesPerRun = new Map<number, number>(
        runs.map((run) => [run.run, run.shelves.length]),
      );
      const topToBottomShare = measureTopToBottomShare(valueGroups, shelvesPerRun);
      const defaultOrient: Orientation =
        topToBottomShare >= verticalBlockShare
          ? "horizontal"
          : horizontalVotes >= measures.length - horizontalVotes
            ? "horizontal"
            : "vertical";
      if (defaultOrient === "vertical") {
        valueOrder[winner.candidate.attr] = inferVerticalValueOrder(
          items,
          winner.candidate.attr,
          runOffsets,
        );
      }

      spine.push({
        attr: winner.candidate.attr,
        label: winner.candidate.label,
        orient: defaultOrient,
      });

      decisive.push(forcedAttr === undefined && contenders.length === 1);
      remaining.splice(
        remaining.findIndex((candidate) => candidate.attr === winner.candidate.attr),
        1,
      );
      scopes = scopes.flatMap((scope) => {
        const groups = splitScope(scope, winner.candidate.attr, valueOrder[winner.candidate.attr]);
        return groups.length < 2 ? [scope] : groups;
      });
    }
    return { levels: spine, decisive };
  };

  const spine = rank([{ path: [], items }], candidates, 0).levels;
  const scopedLevelOrder: Record<string, string[]> = {};

  const rootLevel = spine[0];
  const belowRoot = candidates.filter((candidate) => candidate.attr !== rootLevel?.attr);
  const rootBranches = rootLevel
    ? splitScope({ path: [], items }, rootLevel.attr, valueOrder[rootLevel.attr])
    : [];
  if (rootLevel && rootBranches.length > 1 && belowRoot.length > 0) {
    const planOrder = spine.slice(1).map((level) => level.attr);
    for (const branch of rootBranches) {
      // "" is read at depth 0 of every path; it is not a branch key.
      if (branch.path.some((value) => isPlaceholderValue(value))) continue;
      const branchOrder = rankBranch(
        branch.items,
        belowRoot,
        preferred,
        fragmentJoinCm,
        options.quantityAttribute,
      );
      if (!branchOrder) continue;
      const own = treeCost(branch.items, branchOrder.attrs, fragmentJoinCm);
      const plan = treeCost(branch.items, planOrder, fragmentJoinCm);

      const refined =
        own === plan && branch.items.length >= MIN_ITEMS_TO_JUDGE_GENERICITY
          ? refineBranchOrder(branch.items, branchOrder.attrs, fragmentJoinCm)
          : { attrs: [...branchOrder.attrs], cost: own };
      // The adjacent-swap search may lift the quantity level back over a block level; re-seat it.
      const attrs = pinQuantity(
        refined.attrs,
        options.quantityAttribute,
        (attr) =>
          fragmentScoreOf(branch.items, attr, options.scoreBands?.[attr], fragmentJoinCm) !== null,
      );
      const chosen =
        attrs.join(PATH_SEPARATOR) === refined.attrs.join(PATH_SEPARATOR)
          ? refined
          : { attrs, cost: treeCost(branch.items, attrs, fragmentJoinCm) };
      if (chosen.attrs.join(PATH_SEPARATOR) === planOrder.join(PATH_SEPARATOR)) continue;

      // Must beat the plan's own measure; a tie must not reorder.
      if (chosen.cost < plan) {
        scopedLevelOrder[branch.path.join(PATH_SEPARATOR)] = chosen.attrs;
      }
    }
  }

  const annotations: Pick<InferredSpine, "scopedValueOrder" | "bandOverrides" | "orientOverrides"> =
    { scopedValueOrder: {}, bandOverrides: {}, orientOverrides };
  annotateContract(
    items,
    spine,
    runOffsets,
    annotations,
    0,
    [],
    BAND_SHARE_THRESHOLD,
    options,
    scopedLevelOrder,
  );

  const shelfHints: Record<string, { shelf: number; hFacings: number }[]> = {};
  for (const item of items) {
    shelfHints[item.skuId] = item.placements.map((placement) => ({
      shelf: placement.shelf,
      hFacings: placement.hFacings,
    }));
  }

  return {
    spine,
    scopedLevelOrder,
    valueOrder,
    scopedValueOrder: annotations.scopedValueOrder,
    bandOverrides: annotations.bandOverrides,
    orientOverrides,
    shelfHints,
    levelBands: {},
  };
};

export const inferSpine = (
  items: readonly StudioItem[],
  candidates: readonly AttributeCandidate[],
  runs: readonly StudioRunGeometry[],
  options: InferenceOptions = {},
): InferredSpine => {
  if (options.quantize === false) {
    return inferOverView(items, candidates, runs, options);
  }
  const { levelBands, excluded } = quantizeCandidates(items, candidates);

  const usable = candidates.filter((candidate) => !excluded.includes(candidate.attr));
  if (Object.keys(levelBands).length === 0) {
    return inferOverView(items, usable, runs, options);
  }

  // Bands score; raw values ship unless retainBands.
  if (!options.retainBands) {
    return {
      ...inferOverView(items, usable, runs, { ...options, scoreBands: levelBands }),
      levelBands: {},
    };
  }
  const ranked = inferOverView(bandedView(items, levelBands), usable, runs, options);
  const deepest = ranked.spine[ranked.spine.length - 1]?.attr;
  if (deepest === undefined || levelBands[deepest] === undefined) {
    return { ...ranked, levelBands };
  }
  const kept = Object.fromEntries(Object.entries(levelBands).filter(([attr]) => attr !== deepest));
  const final = inferOverView(bandedView(items, kept), usable, runs, options);
  return { ...final, levelBands: kept };
};

export const draftFromInference = (inference: InferredSpine): StudioDraft => ({
  spine: inference.spine,
  // Omit empty maps: drafts are stringified for history signatures.
  ...(Object.keys(inference.scopedLevelOrder ?? {}).length > 0
    ? { scopedLevelOrder: inference.scopedLevelOrder }
    : {}),
  valueOrder: inference.valueOrder,
  scopedValueOrder: inference.scopedValueOrder,
  bandOverrides: inference.bandOverrides,
  orientOverrides: inference.orientOverrides,
  shelfHints: inference.shelfHints,
  attrOverrides: {},

  ...(Object.keys(inference.levelBands).length > 0 ? { levelBands: inference.levelBands } : {}),
  restrictions: [],
  dismissedFlagIds: [],
  appliedFixFlagIds: [],
});
